import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, Check } from "lucide-react";
import {
  SideBrainTodo,
  SideBrainQuestion,
  SideBrainFile,
  SideBrainRemember,
} from "@/types/sidebrain";

interface TodoCardProps {
  todo: SideBrainTodo;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
}

export function TodoCard({ todo, onToggle, onDelete }: TodoCardProps) {
  return (
    <Card className="mb-2">
      <CardContent className="p-3 flex items-start gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 flex-shrink-0"
          onClick={() => onToggle(todo.id)}
        >
          <div
            className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
              todo.completed
                ? "bg-primary border-primary"
                : "border-muted-foreground"
            }`}
          >
            {todo.completed && (
              <Check className="h-3 w-3 text-primary-foreground" />
            )}
          </div>
        </Button>

        <p
          className={`flex-1 text-sm ${
            todo.completed ? "line-through text-muted-foreground" : ""
          }`}
        >
          {todo.content}
        </p>

        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 flex-shrink-0"
          onClick={() => onDelete(todo.id)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </CardContent>
    </Card>
  );
}

interface QuestionCardProps {
  question: SideBrainQuestion;
  onDelete: (id: string) => void;
  onExpand?: (id: string) => void;
}

export function QuestionCard({
  question,
  onDelete,
  onExpand,
}: QuestionCardProps) {
  return (
    <Card
      className="mb-2 cursor-pointer hover:bg-muted/50"
      onClick={() => onExpand?.(question.id)}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <p className="text-sm font-medium">{question.question}</p>
            {question.answer_summary && (
              <p className="text-xs text-muted-foreground mt-1">
                {question.answer_summary}
              </p>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(question.id);
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface FileCardProps {
  file: SideBrainFile;
  onDelete: (id: string) => void;
  onOpen?: (id: string) => void;
}

export function FileCard({ file, onDelete, onOpen }: FileCardProps) {
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImage = file.file_type.startsWith("image/");

  return (
    <Card
      className="mb-2 cursor-pointer hover:bg-muted/50"
      onClick={() => onOpen?.(file.id)}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          {/* Thumbnail for images */}
          {isImage && file.data_url && (
            <div className="flex-shrink-0">
              <img
                src={file.data_url}
                alt={file.file_name}
                className="w-16 h-16 object-cover rounded border"
              />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{file.file_name}</p>
            <p className="text-xs text-muted-foreground">
              {file.file_type} • {formatFileSize(file.file_size)}
            </p>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(file.id);
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface RememberCardProps {
  remember: SideBrainRemember;
  onDelete: (id: string) => void;
}

export function RememberCard({ remember, onDelete }: RememberCardProps) {
  return (
    <Card className="mb-2">
      <CardContent className="p-3 flex items-start gap-2">
        <p className="flex-1 text-sm">{remember.statement}</p>

        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 flex-shrink-0"
          onClick={() => onDelete(remember.id)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </CardContent>
    </Card>
  );
}
